// Notification Service - API client for notification endpoints
import api from '../lib/api/api';
import type {
  Notification,
  NotificationSettings,
  NotificationFilters,
  NotificationPagination,
  NotificationResponse,
} from '../types/types';

/**
 * Fetch notifications with optional filters and pagination
 */
export const getNotifications = async (
  filters?: NotificationFilters,
  pagination?: NotificationPagination
): Promise<NotificationResponse> => {
  const params = new URLSearchParams();

  // Add filters
  if (filters?.type) params.append('type', filters.type);
  if (filters?.read !== undefined) params.append('read', String(filters.read));
  if (filters?.priority) params.append('priority', filters.priority);

  // Add pagination
  if (pagination?.limit) params.append('limit', String(pagination.limit));
  if (pagination?.offset) params.append('offset', String(pagination.offset));

  const url = `/notifications?${params.toString()}`;
  const response = await api.get(url);
  return response.data as NotificationResponse;
};

/**
 * Get unread notification count
 */
export const getUnreadCount = async (): Promise<number> => {
  const response = await api.get('/notifications/unread-count');
  return (response.data as { count: number }).count;
};

/**
 * Mark a specific notification as read
 */
export const markAsRead = async (notificationId: string): Promise<Notification> => {
  const response = await api.put(`/notifications/${notificationId}/read`, {});
  return response.data as Notification;
};

/**
 * Mark all notifications as read
 */
export const markAllAsRead = async (): Promise<{ count: number; message: string }> => {
  const response = await api.put('/notifications/mark-all-read', {});
  return response.data as { count: number; message: string };
};

/**
 * Delete a specific notification
 */
export const deleteNotification = async (notificationId: string): Promise<void> => {
  await api.delete(`/notifications/${notificationId}`);
};

/**
 * Get user notification settings
 */
export const getNotificationSettings = async (): Promise<NotificationSettings> => {
  const response = await api.get('/notifications/settings');
  return response.data as NotificationSettings;
};

/**
 * Update user notification settings
 */
export const updateNotificationSettings = async (
  settings: Partial<NotificationSettings>
): Promise<NotificationSettings> => {
  const response = await api.put('/notifications/settings', settings);
  return response.data as NotificationSettings;
};

/**
 * Create a test notification (development only)
 */
export const createTestNotification = async (data?: {
  type?: string;
  title?: string;
  message?: string;
  priority?: string;
  link?: string;
}): Promise<Notification> => {
  const response = await api.post('/notifications/test', data);
  return response.data as Notification;
};

const notificationService = {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  getNotificationSettings,
  updateNotificationSettings,
  createTestNotification,
};

export default notificationService;
